import { defineStore } from 'pinia';
import type { Ref } from 'vue';
import { ref } from 'vue';

import type {
  ConversationResultDTO,
  GroupResultDTO,
  MessageResultDTO,
  UserResultDTO,
} from '@nora-monorepo/nora-api-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';

export interface SearchResults {
  messages: {
    results: MessageResultDTO[];
    searching: boolean;
    pagination: {
      total: number;
    };
  };
  conversations: {
    results: ConversationResultDTO[];
    searching: boolean;
    pagination: {
      total: number;
    };
  };
  users: {
    results: UserResultDTO[];
    searching: boolean;
    pagination: {
      total: number;
    };
  };
  groups: {
    results: GroupResultDTO[];
    searching: boolean;
    pagination: {
      total: number;
    };
  };
}

export const SEARCH_STEP = 15;

export const useSearchStore = defineStore('searchStore', () => {
  const searchResults: Ref<SearchResults> = ref<SearchResults>({
    messages: {
      searching: false,
      pagination: {
        total: 0,
      },
      results: [],
    },
    conversations: {
      searching: false,
      pagination: {
        total: 0,
      },
      results: [],
    },
    users: {
      searching: false,
      pagination: {
        total: 0,
      },
      results: [],
    },
    groups: {
      searching: false,
      pagination: {
        total: 0,
      },
      results: [],
    },
  });

  type DetailView = keyof typeof searchResults.value;

  const isSearching = ref(false);
  const showDetailView = ref(false);
  const detailView = ref<DetailView | null>(null);
  const lastQuery = ref('');

  const api = useApiStore();

  function setSearching(newValue: boolean): void {
    isSearching.value = newValue;
  }

  async function search(query: string, replaceResults?: boolean): Promise<void> {
    setSearching(true);

    switch (detailView.value) {
      case 'messages':
        await loadMoreMessages(query, replaceResults);
        break;

      case 'conversations':
        await loadMoreConversations(query, replaceResults);
        break;

      case 'users':
        await loadMoreUsers(query, replaceResults);
        break;

      case 'groups':
        await loadMoreGroups(query, replaceResults);
        break;

      default:
        await Promise.all([searchChat(query), searchUsersAndGroups(query)]);
    }

    lastQuery.value = query;
  }

  async function searchChat(query: string): Promise<void> {
    searchResults.value.messages.searching = true;
    searchResults.value.conversations.searching = true;

    const result = await api.unwrap(
      api.chatSearch.searchConversationsAndMessages({
        query,
        limit: SEARCH_STEP,
        skip: 0,
      }),
    );

    if (result.data) {
      searchResults.value.messages.results = result.data.messages.results;
      searchResults.value.messages.pagination = result.data.messages.pagination;

      searchResults.value.conversations.results
        = result.data.conversations.results;
      searchResults.value.conversations.pagination
        = result.data.conversations.pagination;
    }

    searchResults.value.messages.searching = false;
    searchResults.value.conversations.searching = false;
  }

  async function searchUsersAndGroups(query?: string): Promise<void> {
    searchResults.value.users.searching = true;
    searchResults.value.groups.searching = true;

    const result = await api.unwrap(
      api.nexusSearch.searchUsersAndGroups({
        query,
        limit: SEARCH_STEP,
        skip: 0,
      }),
    );

    if (result.data) {
      searchResults.value.users.results = result.data.users.results;
      searchResults.value.groups.results = result.data.groups.results;

      searchResults.value.users.pagination = result.data.users.pagination;
      searchResults.value.groups.pagination = result.data.groups.pagination;
    }

    searchResults.value.users.searching = false;
    searchResults.value.groups.searching = false;
  }

  async function loadMoreConversations(query: string, replaceResults = false): Promise<void> {
    const result = await api.unwrap(
      api.chatSearch.searchConversations({
        query,
        limit: SEARCH_STEP,
        skip: replaceResults
          ? 0
          : searchResults.value.conversations.results.length,
      }),
    );

    if (result.data) {
      if (replaceResults) {
        searchResults.value.conversations.results = result.data.results;
      }
      else {
        searchResults.value.conversations.results.push(...result.data.results);
      }

      searchResults.value.conversations.pagination = result.data.pagination;
    }
  }

  async function loadMoreMessages(query: string, replaceResults = false): Promise<void> {
    const result = await api.unwrap(
      api.chatSearch.searchMessages({
        query,
        limit: SEARCH_STEP,
        skip: replaceResults ? 0 : searchResults.value.messages.results.length,
      }),
    );

    if (result.data) {
      if (replaceResults) {
        searchResults.value.messages.results = result.data.results;
      }
      else {
        searchResults.value.messages.results.push(...result.data.results);
      }

      searchResults.value.messages.pagination = result.data.pagination;
    }
  }

  async function loadMoreGroups(query: string, replaceResults?: boolean): Promise<void> {
    const result = await api.unwrap(
      api.nexusSearch.searchGroups({
        query,
        limit: SEARCH_STEP,
        skip: replaceResults ? 0 : searchResults.value.groups.results.length,
      }),
    );

    if (result.data) {
      if (!replaceResults) {
        searchResults.value.groups.results.push(...result.data.results);
      }
      else {
        searchResults.value.groups.results = result.data.results;
      }

      searchResults.value.groups.pagination = result.data.pagination;
    }
  }

  async function loadMoreUsers(query: string, replaceResults?: boolean): Promise<void> {
    const result = await api.unwrap(
      api.nexusSearch.searchUsers({
        query,
        limit: SEARCH_STEP,
        skip: replaceResults ? 0 : searchResults.value.users.results.length,
      }),
    );

    if (result.data) {
      if (!replaceResults) {
        searchResults.value.users.results.push(...result.data.results);
      }
      else {
        searchResults.value.users.results = result.data.results;
      }
      searchResults.value.users.pagination = result.data.pagination;
    }
  }

  function clearUsersAndGroupsResults(): void {
    searchResults.value.users.searching = false;
    searchResults.value.users.results = [];
    searchResults.value.users.pagination = { total: 0 };
    searchResults.value.groups.searching = false;
    searchResults.value.groups.results = [];
    searchResults.value.groups.pagination = { total: 0 };
  }

  function clearResults(): void {
    setSearching(false);
    searchResults.value.messages.results = [];
    searchResults.value.conversations.results = [];
    clearUsersAndGroupsResults();
    showDetailView.value = false;
    detailView.value = null;
    lastQuery.value = '';
  }

  function setDetailView(setTo: DetailView): void {
    showDetailView.value = true;
    detailView.value = setTo;
  }

  function closeDetailView(): void {
    showDetailView.value = false;
    detailView.value = null;
  }

  return {
    isSearching,
    searchResults,
    lastQuery,
    showDetailView,
    detailView,
    search,
    searchUsersAndGroups,
    searchChat,
    loadMoreConversations,
    loadMoreMessages,
    loadMoreGroups,
    loadMoreUsers,
    setSearching,
    clearUsersAndGroupsResults,
    clearResults,
    setDetailView,
    closeDetailView,
  };
});
